import React, { useState, useEffect, useRef, useCallback, startTransition } from "react";
import { X, ChevronLeft, ChevronRight, Plus, ShoppingCart, RotateCw, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { addToCart, PRICES, removeFromCart as removeFromCartUtil } from "@/utils/cart";
import { isFavourite, toggleFavourite } from "@/utils/favourites";
import { Participant } from "@/types";
import { useTranslation } from "react-i18next";
import posthogLib from 'posthog-js';
import { supabase } from "@/integrations/supabase/client";
import { getCachedParticipantPhotos } from '@/lib/participantRangesCache';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface Photo {
  id: number;
  url: string;
  loaded: boolean;
}

interface ApplePhotoGalleryProps {
  participant: Participant;
  eventId: string;
  raceId: string; // Internal DB UUID - used for compute-participant-ranges
  swissRaceId: string; // Swiss Equestrian race ID - used for API calls and cart
  eventName: string;
  raceCategory: string;
  onClose: () => void;
  onCartClick?: () => void;
  initialPhotoIndex?: number;
  initialPhotoUrls?: string[]; // Optional: precomputed participant photo URLs
  initialPhotoIds?: number[]; // Optional: real photo IDs corresponding to initialPhotoUrls
  isWinnersGallery?: boolean; // If true, disable collection functionality but allow single photos
  disableCollections?: boolean; // If true, disable only collection purchases but allow single photos
  groupParticipants?: Participant[]; // For grouped races, show all participants in the group
}

export const ApplePhotoGallery: React.FC<ApplePhotoGalleryProps> = ({
  participant,
  eventId,
  raceId,
  swissRaceId,
  eventName,
  raceCategory,
  onClose,
  onCartClick,
  initialPhotoIndex = 0,
  initialPhotoUrls,
  initialPhotoIds,
  isWinnersGallery = false,
  disableCollections = false,
  groupParticipants
}) => {
  const { toast } = useToast();
  const { t } = useTranslation();
  

  // Data
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [currentIndex, setCurrentIndex] = useState(initialPhotoIndex);
  const [loading, setLoading] = useState(true);
  const photosLenRef = useRef(0);
  useEffect(() => {
    photosLenRef.current = photos.length;
  }, [photos.length]);

  // UI chrome
  const [showUI, setShowUI] = useState(true);
  const [lastInteraction, setLastInteraction] = useState(Date.now());

  // Cart
  const [addedItems, setAddedItems] = useState<Set<string>>(new Set());
  const [showCollectionConfirm, setShowCollectionConfirm] = useState(false);

  // Swipe rail (px-based)
  const [dragX, setDragX] = useState(0);
  const [animating, setAnimating] = useState(false);
  const railTransition = animating ? "transform 250ms cubic-bezier(.2,.8,.2,1)" : "none";

  // Zoom & pan
  const [scale, setScale] = useState(1); // 1..4
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const isZoomed = scale > 1;

  // Image rotation
  const [rotation, setRotation] = useState(0); // 0, 90, 180, 270

  // Favourites
  const [isLiked, setIsLiked] = useState(false);
  const [showHeartOverlay, setShowHeartOverlay] = useState(false);

  // Pointers
  type Pt = { id: number; x: number; y: number };
  const pointersRef = useRef<Map<number, Pt>>(new Map());
  const lastMoveRef = useRef<{ x: number; t: number }[]>([]);
  const axisLockRef = useRef<null | "h" | "v">(null);
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const pinchStartScaleRef = useRef(1);
  const pinchStartDistRef = useRef(0);
  const pinchCenterRef = useRef({ x: 0, y: 0 });

  // Double-tap detection (touch)
  const lastTapTimeRef = useRef(0);
  const lastTapPosRef = useRef({ x: 0, y: 0 });
  const movedRef = useRef(false);

  // Refs
  const viewportRef = useRef<HTMLDivElement>(null);
  const thumbsRef = useRef<HTMLDivElement>(null);

  // Constants (YARL-like)
  const MIN_SCALE = 1;
  const MAX_SCALE = 4;
  const ZOOM_IN_MULT = 2;
  const DOUBLE_TAP_MS = 300;
  const DOUBLE_TAP_DIST = 30; // px
  const SWIPE_DIST = 80; // px
  const SWIPE_VEL = 0.35; // px/ms
  const EDGE_RESIST = 0.35;
  const ANGLE_LOCK = 28; // deg

  // Helpers
  const viewportSize = () => {
    const el = viewportRef.current;
    return { w: el?.clientWidth || window.innerWidth || 375, h: el?.clientHeight || window.innerHeight || 667 };
  };
  const W = () => viewportSize().w;

  const clampPan = (x: number, y: number, sc: number) => {
    const { w, h } = viewportSize();
    const maxX = Math.max(0, (w * sc - w) / 2);
    const maxY = Math.max(0, (h * sc - h) / 2);
    return { x: Math.min(maxX, Math.max(-maxX, x)), y: Math.min(maxY, Math.max(-maxY, y)) };
  };

  const resetZoom = useCallback(() => {
    setScale(1);
    setPanX(0);
    setPanY(0);
  }, []);

  const rotateImage = useCallback(() => {
    setRotation((prev) => (prev + 90) % 360);
  }, []);

  const prevPhoto = useCallback(() => {
    setCurrentIndex((i) => {
      if (i <= 0) return i;
      return i - 1;
    });
    setDragX(0);
    resetZoom();
  }, [resetZoom]);

  const nextPhoto = useCallback(() => {
    setCurrentIndex((i) => {
      const max = photosLenRef.current - 1;
      if (i >= max) return i;
      return i + 1;
    });
    setDragX(0);
    resetZoom();
    setRotation(0);
  }, [resetZoom]);

  // Load data + cart init
  useEffect(() => {
    const initCart = () => {
      const cart = JSON.parse(localStorage.getItem("equestrian_cart") || '{"items":[]}');
      const s = new Set<string>();
      cart.items.forEach((it: any) => {
        if (it.participantId === participant.id) {
          if (it.type === "collection") s.add(`${participant.id}-collection`);
          if (it.type === "single" && it.photoId) s.add(`${participant.id}-${it.photoId}`);
        }
      });
      // Wrap state update in startTransition to prevent DOM race conditions
      startTransition(() => {
        setAddedItems(s);
      });
    };
    initCart();
    const onUpd = () => initCart();
    window.addEventListener("cartUpdated", onUpd);
    return () => window.removeEventListener("cartUpdated", onUpd);
  }, [participant.id]);

  const { rider_name, horse_name, swiss_equestrian_rider_id } = participant;
  
  // Token to ignore stale async results when participant changes or fast re-opens
  const loadTokenRef = useRef(0);

  const loadPhotos = useCallback(async () => {
    const token = ++loadTokenRef.current;
    setLoading(true);
    // Reset photos immediately to prevent showing wrong photos
    setPhotos([]);
    
    try {
      // Use pre-loaded data when both URLs and IDs are provided (from parent cache)
      if (initialPhotoUrls && initialPhotoUrls.length > 0 && initialPhotoIds && initialPhotoIds.length === initialPhotoUrls.length) {
        console.log(`[ApplePhotoGallery] Using ${initialPhotoUrls.length} pre-cached photos (skipping edge function)`);
        const cachedPhotos = initialPhotoUrls.map((url: string, idx: number) => ({
          id: initialPhotoIds[idx],
          url,
          loaded: false
        }));
        if (loadTokenRef.current === token) {
          setPhotos(cachedPhotos);
        }
      } else if (isWinnersGallery && initialPhotoUrls && initialPhotoUrls.length > 0) {
        console.log(`Loading ${initialPhotoUrls.length} winners photos`);
        const winnersPhotos = initialPhotoUrls.map((url: string, idx: number) => {
          const match = url.match(/\/winners\/(\d+)(?:\.[\w-]+)?$/);
          const realId = initialPhotoIds?.[idx] ?? (match ? Number(match[1]) : idx + 1);
          return {
            id: realId,
            url,
            loaded: false
          };
        });
        
        if (loadTokenRef.current === token) {
          setPhotos(winnersPhotos);
        }
      } else {
        // Check shared cache before calling edge function
        const cachedData = getCachedParticipantPhotos(raceId, eventId, participant.id);
        if (cachedData && cachedData.urls.length > 0 && cachedData.fileIds.length === cachedData.urls.length) {
          console.log(`[ApplePhotoGallery] Using shared cache for ${participant.rider_name} (${cachedData.urls.length} photos)`);
          const cachedPhotos = cachedData.urls.map((url: string, idx: number) => ({
            id: cachedData.fileIds[idx],
            url,
            loaded: false
          }));
          if (loadTokenRef.current === token) {
            setPhotos(cachedPhotos);
          }
        } else {
        // Fallback: call compute-participant-ranges
        const { data, error } = await supabase.functions.invoke('compute-participant-ranges', {
          body: {
            raceId,
            eventId
          }
        });

        if (error) {
          console.error('Error from compute-participant-ranges:', error);
          throw new Error(error.message || 'Failed to fetch photo ranges');
        }

        const participantRange = data.participants?.find((p: any) => p.id === participant.id);
        
        if (participantRange && participantRange.photo_range) {
          const { urls, file_ids } = participantRange.photo_range;
          console.log(`Loading ${urls.length} photos for ${participant.rider_name} (${participant.horse_name})`);
          
          const fullPhotos = urls.map((url: string, idx: number) => ({
            id: Number(file_ids[idx]) || idx + 1,
            url,
            loaded: false
          }));
          
          if (loadTokenRef.current === token) {
            setPhotos(fullPhotos);
          }
        } else {
          console.log(`No photos found for participant ${participant.rider_name}`);
          if (loadTokenRef.current === token) {
            setPhotos([]);
          }
        }
      }
      }
    } catch (e) {
      console.error('Error loading photos:', e);
      toast({ title: 'Error', description: 'Error loading photos', variant: 'destructive' });
      if (loadTokenRef.current === token) {
        setPhotos([]); // Ensure we clear photos on error
      }
    } finally {
      if (loadTokenRef.current === token) {
        setLoading(false);
      }
    }
  }, [participant.id, participant.rider_name, participant.horse_name, eventId, raceId, toast, isWinnersGallery, initialPhotoUrls, initialPhotoIds]);
  
  // Reset state when participant changes
  useEffect(() => {
    setCurrentIndex(initialPhotoIndex);
    setPhotos([]);
    resetZoom();
    setRotation(0);
  }, [participant.id, initialPhotoIndex, resetZoom]);

  useEffect(() => {
    loadPhotos();
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [loadPhotos]);

  // Track liked state when photo or photos array changes
  useEffect(() => {
    const cp = photos[currentIndex];
    if (cp) {
      setIsLiked(isFavourite(cp.id, eventId));
    }
  }, [currentIndex, photos, eventId]);

  // Listen for external favourites changes
  useEffect(() => {
    const onFavUpdate = () => {
      const cp = photos[currentIndex];
      if (cp) setIsLiked(isFavourite(cp.id, eventId));
    };
    window.addEventListener('favouritesUpdated', onFavUpdate);
    return () => window.removeEventListener('favouritesUpdated', onFavUpdate);
  }, [currentIndex, photos, eventId]);

  const handleToggleFavourite = useCallback((source: 'button' | 'double_tap') => {
    const cp = photos[currentIndex];
    if (!cp) return;
    const liked = toggleFavourite({
      photoId: cp.id,
      participantId: participant.id,
      swissEquestrianRiderId: swiss_equestrian_rider_id || '',
      eventId,
      raceId: swissRaceId,
      eventName,
      raceCategory,
      riderName: rider_name,
      horseName: horse_name,
      photoUrl: cp.url,
    });
    setIsLiked(liked);

    // Show heart overlay animation when liking
    if (liked) {
      setShowHeartOverlay(true);
      setTimeout(() => setShowHeartOverlay(false), 800);
    }

    // Track PostHog event
    try {
      if (posthogLib.__loaded) {
        posthogLib.capture('photo_liked', {
          photo_id: cp.id,
          participant_id: participant.id,
          event_id: eventId,
          race_category: raceCategory,
          source,
          action: liked ? 'liked' : 'unliked',
        });
      }
    } catch (e) {
      console.warn('Error capturing photo_liked event:', e);
    }
  }, [photos, currentIndex, participant.id, eventId, swissRaceId, eventName, raceCategory, rider_name, horse_name]);

  // Inactivity hide
  useEffect(() => {
    const move = () => {
      setLastInteraction(Date.now());
      setShowUI(true);
    };
    const key = () => {
      setLastInteraction(Date.now());
      setShowUI(true);
    };
    const id = setInterval(() => {
      if (Date.now() - lastInteraction > 3000) setShowUI(false);
    }, 500);
    window.addEventListener("mousemove", move);
    window.addEventListener("keydown", key);
    return () => {
      clearInterval(id);
      window.removeEventListener("mousemove", move);
      window.removeEventListener("keydown", key);
    };
  }, [lastInteraction]);

  // ===== ZOOM HELPERS =====
  const zoomAt = useCallback(
    (mult: number, clientX?: number, clientY?: number) => {
      const vp = viewportRef.current;
      const newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, scale * mult));
      if (!vp) {
        setScale(newScale);
        return;
      }
      const rect = vp.getBoundingClientRect();
      const cx = (clientX ?? rect.left + rect.width / 2) - rect.left - rect.width / 2;
      const cy = (clientY ?? rect.top + rect.height / 2) - rect.top - rect.height / 2;
      const scaleDelta = newScale / scale;
      const nx = panX - cx * (scaleDelta - 1);
      const ny = panY - cy * (scaleDelta - 1);
      const clamped = clampPan(nx, ny, newScale);
      setPanX(clamped.x);
      setPanY(clamped.y);
      setScale(newScale);
    },
    [scale, panX, panY]
  );

  // ===== Keyboard (desktop arrows + +/-/0) =====
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable) {
        return;
      }
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        prevPhoto();
      }
      if (e.key === "ArrowRight") {
        e.preventDefault();
        nextPhoto();
      }
      if (e.key === " ") {
        e.preventDefault();
        setShowUI((v) => !v);
      }
      if (e.key === "+" || e.key === "=") {
        e.preventDefault();
        zoomAt(ZOOM_IN_MULT);
      }
      if (e.key === "-") {
        e.preventDefault();
        zoomAt(1 / ZOOM_IN_MULT);
      }
      if (e.key === "0") {
        e.preventDefault();
        resetZoom();
      }
      if (e.key === "r" || e.key === "R") {
        e.preventDefault();
        rotateImage();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, prevPhoto, nextPhoto, zoomAt, resetZoom, rotateImage]);

  // ===== Pointer events: swipe + pinch + mobile double-tap =====
  // IMPORTANT FIX: include `loading` in deps so listeners are attached
  // after the loader disappears and the viewport exists.
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;

    const onPointerDown = (e: PointerEvent) => {
      setShowUI(true);
      setLastInteraction(Date.now());
      el.setPointerCapture?.(e.pointerId);
      pointersRef.current.set(e.pointerId, { id: e.pointerId, x: e.clientX, y: e.clientY });

      if (pointersRef.current.size === 1) {
        axisLockRef.current = null;
        lastMoveRef.current = [{ x: e.clientX, t: performance.now() }];
        startXRef.current = e.clientX;
        startYRef.current = e.clientY;
        movedRef.current = false;
        setAnimating(false);
      } else if (pointersRef.current.size === 2) {
        const pts = Array.from(pointersRef.current.values());
        const dx = pts[0].x - pts[1].x;
        const dy = pts[0].y - pts[1].y;
        pinchStartDistRef.current = Math.hypot(dx, dy);
        pinchStartScaleRef.current = scale;
        pinchCenterRef.current = { x: (pts[0].x + pts[1].x) / 2, y: (pts[0].y + pts[1].y) / 2 };
      }
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!pointersRef.current.has(e.pointerId)) return;
      const prev = pointersRef.current.get(e.pointerId)!;
      const nowPt = { id: e.pointerId, x: e.clientX, y: e.clientY };
      pointersRef.current.set(e.pointerId, nowPt);

      const pts = Array.from(pointersRef.current.values());

      // Pinch
      if (pts.length === 2) {
        const dx = pts[0].x - pts[1].x;
        const dy = pts[0].y - pts[1].y;
        const dist = Math.hypot(dx, dy);
        const rawScale = (pinchStartScaleRef.current * dist) / Math.max(1, pinchStartDistRef.current);
        const newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, rawScale));

        const vp = viewportRef.current!;
        const rect = vp.getBoundingClientRect();
        const cx = pinchCenterRef.current.x - rect.left - rect.width / 2;
        const cy = pinchCenterRef.current.y - rect.top - rect.height / 2;
        const scaleDelta = newScale / scale;
        const nx = panX - cx * (scaleDelta - 1);
        const ny = panY - cy * (scaleDelta - 1);
        const clamped = clampPan(nx, ny, newScale);
        setPanX(clamped.x);
        setPanY(clamped.y);
        setScale(newScale);
        return;
      }

      // Single finger: if zoomed -> pan image
      if (scale > 1) {
        const dx = nowPt.x - prev.x;
        const dy = nowPt.y - prev.y;
        if (Math.abs(dx) > 3 || Math.abs(dy) > 3) movedRef.current = true;
        const clamped = clampPan(panX + dx, panY + dy, scale);
        setPanX(clamped.x);
        setPanY(clamped.y);
        return;
      }

      // Single finger, scale===1 -> gallery swipe
      const dxAbs = Math.abs(nowPt.x - startXRef.current);
      const dyAbs = Math.abs(nowPt.y - startYRef.current);
      if (dxAbs > 3 || dyAbs > 3) movedRef.current = true;

      if (!axisLockRef.current) {
        const ang = (Math.atan2(dyAbs, dxAbs) * 180) / Math.PI;
        axisLockRef.current = ang <= ANGLE_LOCK ? "h" : "v";
      }
      if (axisLockRef.current === "v") return;

      let newDx = nowPt.x - startXRef.current;
      const atFirst = currentIndex === 0;
      const atLast = currentIndex === photosLenRef.current - 1;
      if ((atFirst && newDx > 0) || (atLast && newDx < 0)) newDx = newDx * EDGE_RESIST;

      setDragX(newDx);
      const t = performance.now();
      lastMoveRef.current.push({ x: nowPt.x, t });
      lastMoveRef.current = lastMoveRef.current.filter((p) => t - p.t <= 120);
    };

    const onPointerUp = (e: PointerEvent) => {
      if (!pointersRef.current.has(e.pointerId)) return;
      pointersRef.current.delete(e.pointerId);

      // If pinch still active, bail
      if (pointersRef.current.size >= 1) return;

      // Mobile double-tap toggle zoom (only if it was essentially a tap, not a swipe)
      if (e.pointerType === "touch") {
        const now = performance.now();
        const dt = now - lastTapTimeRef.current;
        const dx = e.clientX - lastTapPosRef.current.x;
        const dy = e.clientY - lastTapPosRef.current.y;
        const dist = Math.hypot(dx, dy);

        if (!movedRef.current && dt < DOUBLE_TAP_MS && dist < DOUBLE_TAP_DIST) {
          if (scale === 1) {
            // Double-tap to like (Instagram-style), not zoom
            handleToggleFavourite('double_tap');
          } else {
            resetZoom();
          }
          lastTapTimeRef.current = 0;
          return;
        }
        // record this tap
        if (!movedRef.current) {
          lastTapTimeRef.current = now;
          lastTapPosRef.current = { x: e.clientX, y: e.clientY };
        }
      }

      // End swipe / pan
      if (scale > 1) {
        return;
      }

      // Decide slide change
      let v = 0;
      if (lastMoveRef.current.length >= 2) {
        const first = lastMoveRef.current[0];
        const last = lastMoveRef.current[lastMoveRef.current.length - 1];
        v = (last.x - first.x) / Math.max(1, last.t - first.t); // px/ms
      }
      const goPrev = dragX > SWIPE_DIST || v > SWIPE_VEL;
      const goNext = dragX < -SWIPE_DIST || v < -SWIPE_VEL;

      if (goPrev) {
        setAnimating(true);
        setDragX(W());
        setTimeout(() => {
          setAnimating(false);
          setDragX(0);
          prevPhoto();
        }, 250);
      } else if (goNext) {
        setAnimating(true);
        setDragX(-W());
        setTimeout(() => {
          setAnimating(false);
          setDragX(0);
          nextPhoto();
        }, 250);
      } else {
        // snap back
        setAnimating(true);
        setDragX(0);
        setTimeout(() => setAnimating(false), 250);
      }

      axisLockRef.current = null;
      lastMoveRef.current = [];
    };

    el.addEventListener("pointerdown", onPointerDown);
    el.addEventListener("pointermove", onPointerMove);
    el.addEventListener("pointerup", onPointerUp);
    el.addEventListener("pointercancel", onPointerUp);
    el.addEventListener("pointerleave", onPointerUp);

    return () => {
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerup", onPointerUp);
      el.removeEventListener("pointercancel", onPointerUp);
      el.removeEventListener("pointerleave", onPointerUp);
    };
  }, [
    loading,
    currentIndex,
    zoomAt,
    resetZoom,
    prevPhoto,
    nextPhoto,
    scale,
    panX,
    panY,
    handleToggleFavourite,
  ]);

  // Single-click to zoom on DESKTOP only.
  const onImageClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const touchCap =
      (typeof navigator !== "undefined" && navigator.maxTouchPoints > 0) ||
      (typeof window !== "undefined" && "ontouchstart" in window);
    if (touchCap) return; // mobile handled by double-tap

    if (scale === 1) {
      zoomAt(ZOOM_IN_MULT, e.clientX, e.clientY);
    } else {
      resetZoom();
    }
    setLastInteraction(Date.now());
  };

  // Wheel: ctrl/⌘ + wheel => zoom, else if zoomed => pan
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const delta = -e.deltaY;
        const factor = Math.exp(delta * 0.0015);
        zoomAt(factor, e.clientX, e.clientY);
      } else if (isZoomed) {
        e.preventDefault();
        const clamped = clampPan(panX - e.deltaX, panY - e.deltaY, scale);
        setPanX(clamped.x);
        setPanY(clamped.y);
      }
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel as EventListener);
  }, [zoomAt, isZoomed, panX, panY, scale]);

  // Thumbnails auto-center
  useEffect(() => {
    if (!thumbsRef.current) return;
    const tw = 72;
    const cw = thumbsRef.current.clientWidth;
    const scrollLeft = currentIndex * tw - cw / 2 + tw / 2;
    thumbsRef.current.scrollTo({ left: Math.max(0, scrollLeft), behavior: "smooth" });
  }, [currentIndex]);

  // Cart ops
  const removeFromCart = (type: "single" | "collection", photoId?: number) => {
    removeFromCartUtil(participant.id, type, photoId);
    const key = type === "single" ? `${participant.id}-${photoId}` : `${participant.id}-collection`;
    setAddedItems((prev) => {
      const s = new Set(prev);
      s.delete(key);
      return s;
    });
  };

  const addItemToCart = (type: "single" | "collection", photoId?: number) => {
    const key = type === "single" ? `${participant.id}-${photoId}` : `${participant.id}-collection`;
    
    addToCart({
      type,
      participantId: participant.id,
      swissEquestrianRiderId: swiss_equestrian_rider_id,
      participantName: rider_name,
      horseName: participant.horse_name || "",
      raceCategory,
      eventName,
      eventId,
      raceId: swissRaceId, // Use Swiss race ID for cart/API calls
      photoId: type === "single" ? photoId : undefined,
      price: type === "single" ? PRICES.SINGLE_PHOTO : PRICES.COLLECTION,
      isGroup: (participant as any).isGroup || false, // Flag to indicate if this is from a group
      isWinner: isWinnersGallery || false, // Flag to indicate if this is from a winner ceremony
    }, posthogLib.__loaded ? posthogLib : null); // Pass PostHog instance for tracking
    setAddedItems((prev) => new Set(prev).add(key));
  };

  const handleAddToCart = (type: "single" | "collection", photoId?: number) => {
    console.log('handleAddToCart called:', { type, photoId, participantId: participant.id });
    const key = type === "single" ? `${participant.id}-${photoId}` : `${participant.id}-collection`;
    const collectionKey = `${participant.id}-collection`;
    if (addedItems.has(key)) {
      console.log('Removing from cart:', key);
      removeFromCart(type, photoId);
      return;
    }
    if (type === "collection") {
      const cart = JSON.parse(localStorage.getItem("equestrian_cart") || '{"items":[]}');
      const hasSingles = cart.items.some((i: any) => i.participantId === participant.id && i.type === "single");
      if (hasSingles) {
        setShowCollectionConfirm(true);
        return;
      }
    }
    if (type === "single" && addedItems.has(collectionKey)) {
      toast({ title: t("gallery.collectionPriority"), description: t("gallery.collectionContainsAll"), variant: "destructive" });
      return;
    }
    addItemToCart(type, photoId);
  };

  const handleCollectionConfirm = () => {
    const cart = JSON.parse(localStorage.getItem("equestrian_cart") || '{"items":[]}');
    const filtered = cart.items.filter((it: any) => !(it.participantId === participant.id && it.type === "single"));
    localStorage.setItem("equestrian_cart", JSON.stringify({ items: filtered }));
    setAddedItems((prev) => {
      const s = new Set(prev);
      [...s].forEach((k) => {
        if (k.startsWith(`${participant.id}-`) && k !== `${participant.id}-collection`) s.delete(k);
      });
      return s;
    });
    addItemToCart("collection");
    setShowCollectionConfirm(false);
    window.dispatchEvent(new CustomEvent("cartUpdated"));
  };

  const currentPhoto = photos[currentIndex];
  const isCollectionInCart = addedItems.has(`${participant.id}-collection`);
  const isCurrentPhotoInCart = currentPhoto ? addedItems.has(`${participant.id}-${currentPhoto.id}`) : false;

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black z-[1900] flex items-center justify-center">
        <div className="text-white">{t("gallery.loading")}</div>
      </div>
    );
  }

  if (photos.length === 0) {
    return (
      <div className="fixed inset-0 bg-black z-[1900] flex items-center justify-center">
        <div className="text-white text-center">
          <p className="mb-4">{t("gallery.noPhotos")}</p>
          <Button onClick={onClose} variant="outline" className="bg-white/20 text-white border-white/30 hover:bg-white/30">
            {t("gallery.close")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 bg-black z-[1900] overflow-hidden"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          setShowUI((v) => !v);
          setLastInteraction(Date.now());
        }
      }}
      style={{ pointerEvents: showCollectionConfirm ? 'none' : 'auto' }}
    >
      {/* Header */}
      <div
        className={`absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/80 to-transparent p-4 transition-opacity duration-300 ${
          showUI ? "opacity-100" : "opacity-0"
        }`}
      >
        <div className="flex items-center justify-between text-white">
          <div>
            <h2 className="font-semibold text-white">{rider_name}</h2>
            <p className="text-sm text-white drop-shadow-sm">{horse_name}</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="bg-white/20 text-white border-none">
              {t("gallery.photoCounter", { current: currentIndex + 1, total: photos.length })}
            </Badge>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={(e) => {
                e.stopPropagation();
                rotateImage();
              }} 
              className="hover:bg-white/20 text-white"
              title="Rotate image (R)"
            >
              <RotateCw className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={onClose} className="text-white hover:bg-white/20">
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Viewport (pointer events target) */}
      <div
        ref={viewportRef}
        className="absolute inset-0 overflow-hidden"
        style={{ touchAction: isZoomed ? "none" : "pan-y" }}
      >
        {/* Prev */}
        {currentIndex > 0 && photos[currentIndex - 1] && (
          <div
            className="absolute inset-0 flex items-center justify-center will-change-transform pointer-events-none"
            style={{ transform: `translate3d(${dragX - W()}px,0,0)`, transition: railTransition }}
            aria-hidden
          >
            <img
              src={photos[currentIndex - 1].url}
              alt=""
              className="max-w-full max-h-full object-contain select-none"
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
              style={{ userSelect: "none", WebkitUserSelect: "none" }}
            />
          </div>
        )}

        {/* Current */}
        {currentPhoto && (
          <div
            className="absolute inset-0 flex items-center justify-center will-change-transform"
            style={{ transform: `translate3d(${dragX}px,0,0)`, transition: railTransition }}
          >
            <img
              src={currentPhoto.url}
              alt={`Photo ${currentPhoto.id}`}
              className="max-w-full max-h-full object-contain select-none"
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
              onClick={(e) => {
                // single click zoom on desktop only
                const touchCap =
                  (typeof navigator !== "undefined" && navigator.maxTouchPoints > 0) ||
                  (typeof window !== "undefined" && "ontouchstart" in window);
                if (!touchCap) {
                  e.stopPropagation();
                  if (scale === 1) zoomAt(ZOOM_IN_MULT, e.clientX, e.clientY);
                  else resetZoom();
                  setLastInteraction(Date.now());
                }
              }}
              style={{
                userSelect: "none",
                WebkitUserSelect: "none",
                transform: `translate3d(${panX}px, ${panY}px, 0) scale(${scale}) rotate(${rotation}deg)`,
                transition: animating ? "transform 120ms ease-out" : "none",
                cursor: isZoomed ? "grab" : "zoom-in",
              }}
            />
          </div>
        )}

        {/* Next */}
        {currentIndex < photos.length - 1 && photos[currentIndex + 1] && (
          <div
            className="absolute inset-0 flex items-center justify-center will-change-transform pointer-events-none"
            style={{ transform: `translate3d(${dragX + W()}px,0,0)`, transition: railTransition }}
            aria-hidden
          >
            <img
              src={photos[currentIndex + 1].url}
              alt=""
              className="max-w-full max-h-full object-contain select-none"
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
              style={{ userSelect: "none", WebkitUserSelect: "none" }}
            />
          </div>
        )}
      </div>

      {/* Heart overlay animation (Instagram-style) */}
      {showHeartOverlay && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-[2050]">
          <Heart className="w-24 h-24 fill-red-500 text-red-500 animate-heart-overlay drop-shadow-lg" />
        </div>
      )}

      {/* Arrows */}
      {currentIndex > 0 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            prevPhoto();
          }}
          className={`absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/50 text-white flex items-center justify-center transition-opacity duration-300 hover:bg-black/70 ${
            showUI ? "opacity-100" : "opacity-0"
          }`}
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
      )}
      {currentIndex < photos.length - 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            nextPhoto();
          }}
          className={`absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/50 text-white flex items-center justify-center transition-opacity duration-300 hover:bg-black/70 ${
            showUI ? "opacity-100" : "opacity-0"
          }`}
        >
          <ChevronRight className="w-6 h-6" />
        </button>
      )}

      {/* Bottom toolbar */}
      <div
        className={`absolute bottom-0 left-0 right-0 z-[2100] bg-gradient-to-t from-black/80 to-transparent p-4 transition-opacity duration-300 ${
          showUI ? "opacity-100" : "opacity-0"
        }`}
      >
        <div className="flex items-center justify-center gap-2 md:gap-4 text-white">
          <Button
            variant="secondary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              const p = currentPhoto;
              if (p) handleAddToCart("single", p.id);
            }}
            disabled={isCollectionInCart}
            className={`bg-white/20 text-white border-none ${
              isCollectionInCart
                ? "opacity-60 cursor-not-allowed"
                : isCurrentPhotoInCart
                ? "bg-red-500/20 hover:bg-red-500/30"
                : "hover:bg-white/30"
            } text-xs md:text-sm px-2 md:px-3 relative z-[2000]`}
            style={{ pointerEvents: 'auto' }}
          >
            {isCurrentPhotoInCart ? (
              <>
                <X className="w-4 h-4 mr-1 md:mr-2" />
                {t("gallery.removePhoto", { price: PRICES.SINGLE_PHOTO })}
              </>
            ) : (
              <>
                <Plus className="w-4 h-4 mr-1 md:mr-2" />
                {t("gallery.singlePhotoButton", { price: PRICES.SINGLE_PHOTO })}
              </>
            )}
          </Button>

          {/* Like button */}
          <Button
            variant="secondary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleToggleFavourite('button');
            }}
            className={`bg-white/20 text-white border-none hover:bg-white/30 px-2 md:px-3 relative z-[2000] active:scale-95 transition-transform`}
            style={{ pointerEvents: 'auto' }}
          >
            <Heart className={`w-4 h-4 transition-all ${isLiked ? 'fill-red-500 text-red-500 animate-heart-pop' : ''}`} />
          </Button>

          {/* Only show collection button if not disabled for winners gallery */}
          {!isWinnersGallery && !disableCollections && (
            <Button
              variant="secondary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleAddToCart("collection");
              }}
              className={`bg-white/20 text-white border-none ${
                isCollectionInCart ? "bg-red-500/20 hover:bg-red-500/30" : "hover:bg-white/30"
              } text-xs md:text-sm px-2 md:px-3 relative z-[2000]`}
              style={{ pointerEvents: 'auto' }}
            >
              {isCollectionInCart ? (
                <>
                  <X className="w-4 h-4 mr-1 md:mr-2" />
                  {t("gallery.removeCollection", { price: PRICES.COLLECTION })}
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-1 md:mr-2" />
                  {t("gallery.collectionButton", { price: PRICES.COLLECTION })}
                </>
              )}
            </Button>
          )}

          {onCartClick && (
            <Button
              variant="secondary"
              size="sm"
              onClick={(e) => {
                console.log('Cart button clicked');
                e.stopPropagation();
                e.preventDefault();
                onCartClick();
              }}
              className="bg-white/20 text-white border-none hover:bg-white/30 px-2 relative z-[2000]"
              style={{ pointerEvents: 'auto' }}
            >
              <ShoppingCart className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Thumbnails */}
      <div
        className={`absolute bottom-20 left-0 right-0 z-10 transition-opacity duration-300 ${
          showUI ? "opacity-100" : "opacity-0"
        }`}
      >
        <div
          ref={thumbsRef}
          className="flex gap-2 px-4 py-2 overflow-x-auto scrollbar-hide"
          style={{ scrollBehavior: "smooth" }}
          data-thumbnail-area
        >
          {photos.map((p, i) => (
            <button
              key={p.id}
              onClick={(e) => {
                e.stopPropagation();
                setCurrentIndex(i);
                resetZoom();
                setDragX(0);
              }}
              className={`flex-shrink-0 w-16 h-12 rounded border-2 overflow-hidden transition-all duration-200 ${
                i === currentIndex ? "border-white scale-110" : "border-white/30"
              }`}
            >
              <img
                src={p.url}
                alt={`Thumbnail ${p.id}`}
                className="w-full h-full object-cover select-none"
                draggable={false}
                onContextMenu={(e) => e.preventDefault()}
              />
            </button>
          ))}
        </div>
      </div>

      {/* Confirm collection */}
      <AlertDialog open={showCollectionConfirm} onOpenChange={setShowCollectionConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("gallery.confirmCollection")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("gallery.confirmCollectionDescription", {
                rider: rider_name,
                horse: horse_name,
                category: raceCategory,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("gallery.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleCollectionConfirm}>{t("gallery.confirmSwitch")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};